import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { XCircleIcon, Bold, Italic, Underline, Strikethrough } from "lucide-react";
import { Textarea } from "./textarea";
import { Button } from "./button";
import { cn } from "../lib/utils";
export function SimpleMarkdownEditor({ value, onChange, placeholder, id, name, rows = 4, maxLength, enabledFormats = {
    bold: true,
    italic: true,
    underline: true,
    strikethrough: true,
}, }) {
    const [isFocused, setIsFocused] = useState(false);
    const textareaRef = useRef(null);
    const formatText = (prefix, suffix) => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = value.substring(start, end);
        const actualSuffix = suffix || prefix;
        const newText = value.substring(0, start) +
            prefix + selectedText + actualSuffix +
            value.substring(end);
        onChange(newText);
        // Restore cursor position
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + prefix.length + selectedText.length + actualSuffix.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };
    const formatBold = () => formatText("**");
    const formatItalic = () => formatText("*");
    const formatUnderline = () => formatText("__");
    const formatStrikethrough = () => formatText("~~");
    return (_jsxs("div", { className: "space-y-2", children: [_jsxs("div", { className: "flex gap-1 p-2 bg-gray-50 rounded-md border", children: [enabledFormats.bold && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: formatBold, className: "h-8 w-8 p-0", title: "Bold (**text**)", children: _jsx(Bold, { className: "h-4 w-4" }) })), enabledFormats.italic && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: formatItalic, className: "h-8 w-8 p-0", title: "Italic (*text*)", children: _jsx(Italic, { className: "h-4 w-4" }) })), enabledFormats.underline && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: formatUnderline, className: "h-8 w-8 p-0", title: "Underline (__text__)", children: _jsx(Underline, { className: "h-4 w-4" }) })), enabledFormats.strikethrough && (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: formatStrikethrough, className: "h-8 w-8 p-0", title: "Strikethrough (~~text~~)", children: _jsx(Strikethrough, { className: "h-4 w-4" }) }))] }), _jsxs("div", { className: "relative", children: [_jsx(Textarea, { ref: textareaRef, id: id, name: name || id, value: value, onChange: (e) => onChange(e.target.value), placeholder: placeholder, rows: rows, className: cn("border-gray-300 rounded-md pr-10", isFocused && "ring-2 ring-blue-500 border-blue-500"), maxLength: maxLength, onFocus: () => setIsFocused(true), onBlur: () => setTimeout(() => setIsFocused(false), 100) }), value && isFocused && (_jsx("button", { type: "button", onClick: () => {
                            onChange("");
                            textareaRef.current?.focus();
                        }, className: "absolute top-2 right-2 flex items-center p-1", children: _jsx(XCircleIcon, { className: "h-5 w-5 text-gray-400", "aria-hidden": "true" }) }))] }), _jsxs("div", { className: "text-xs text-gray-500", children: ["Use ", [
                        enabledFormats.bold && "**bold**",
                        enabledFormats.italic && "*italic*",
                        enabledFormats.underline && "__underline__",
                        enabledFormats.strikethrough && "~~strikethrough~~"
                    ].filter(Boolean).join(", ")] })] }));
}
